import { useEffect, useState, useRef, useCallback } from 'react'
import { useAdmin } from '@/hooks/useAdmin'
import type { DbTableSummary, DbTableData, DbColumn, DbQueryResult } from '@/hooks/useAdmin'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { PaginationControls } from '@/components/ui/pagination-controls'
import { Database, Play, AlertTriangle, Loader2, Table2 } from 'lucide-react'

const PAGE_SIZE = 50

// ── Cell formatter ─────────────────────────────────────────────────────────────
function formatCell(val: unknown): string {
  if (val === null || val === undefined) return ''
  if (typeof val === 'object') return JSON.stringify(val)
  return String(val)
}

// ── Schema table ──────────────────────────────────────────────────────────────
function SchemaTable({ columns }: { columns: DbColumn[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 px-4 font-medium">Column</th>
            <th className="py-2 px-4 font-medium">Type</th>
            <th className="py-2 px-4 font-medium">Nullable</th>
          </tr>
        </thead>
        <tbody>
          {columns.map((col) => (
            <tr key={col.column_name} className="border-b last:border-0 hover:bg-accent/20 transition-colors">
              <td className="py-2 px-4 font-mono text-sm text-foreground">{col.column_name}</td>
              <td className="py-2 px-4 font-mono text-xs text-muted-foreground">{col.data_type}</td>
              <td className="py-2 px-4 text-xs text-muted-foreground">{col.is_nullable}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Generic data rows table ───────────────────────────────────────────────────
function DataTable({
  columns,
  rows,
}: {
  columns: string[]
  rows: Record<string, unknown>[]
}) {
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-muted-foreground">
        <Table2 className="h-7 w-7 opacity-40" />
        <p className="text-sm">No rows</p>
      </div>
    )
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs text-muted-foreground">
            {columns.map((col) => (
              <th key={col} className="py-2 px-4 font-medium whitespace-nowrap">{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-0 hover:bg-accent/20 transition-colors">
              {columns.map((col) => (
                <td
                  key={col}
                  className="py-2 px-4 font-mono text-xs text-muted-foreground max-w-[280px] truncate"
                  title={formatCell(row[col])}
                >
                  {formatCell(row[col]) || <span className="text-muted-foreground/40 italic">null</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Browse tab ────────────────────────────────────────────────────────────────
function BrowseTab() {
  const { listTables, getTable } = useAdmin()

  const [tables, setTables] = useState<DbTableSummary[]>([])
  const [tablesLoading, setTablesLoading] = useState(true)
  const [tablesError, setTablesError] = useState<string | null>(null)

  const [selectedTable, setSelectedTable] = useState<string | null>(null)
  const [tableData, setTableData] = useState<DbTableData | null>(null)
  const [tableLoading, setTableLoading] = useState(false)
  const [tableError, setTableError] = useState<string | null>(null)

  const [page, setPage] = useState(1)

  useEffect(() => {
    setTablesLoading(true)
    listTables()
      .then(({ tables: t }) => setTables(t))
      .catch(() => setTablesError('Failed to load tables'))
      .finally(() => setTablesLoading(false))
  }, [listTables])

  const loadTable = useCallback(
    (name: string, p: number) => {
      setTableLoading(true)
      setTableError(null)
      getTable(name, { limit: PAGE_SIZE, offset: (p - 1) * PAGE_SIZE })
        .then((d) => setTableData(d))
        .catch(() => setTableError('Failed to load table data'))
        .finally(() => setTableLoading(false))
    },
    [getTable],
  )

  const handleSelectTable = (name: string) => {
    setSelectedTable(name)
    setTableData(null)
    setPage(1)
    loadTable(name, 1)
  }

  const handlePageChange = (p: number) => {
    setPage(p)
    if (selectedTable) loadTable(selectedTable, p)
  }

  const totalPages = tableData ? Math.max(1, Math.ceil(tableData.total / PAGE_SIZE)) : 1

  return (
    <div className="flex gap-4 h-full min-h-0">
      {/* Left: table list */}
      <Card className="w-56 shrink-0 overflow-hidden flex flex-col">
        <div className="px-3 py-2.5 border-b shrink-0">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Tables</p>
        </div>
        <div className="overflow-y-auto flex-1">
          {tablesLoading && (
            <div className="p-3 space-y-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-4 rounded bg-muted/40 animate-pulse" style={{ width: `${55 + i * 5}%` }} />
              ))}
            </div>
          )}
          {tablesError && (
            <p className="p-3 text-xs text-destructive">{tablesError}</p>
          )}
          {!tablesLoading && !tablesError && tables.length === 0 && (
            <p className="p-3 text-xs text-muted-foreground">No tables found</p>
          )}
          {!tablesLoading && tables.map((t) => (
            <button
              key={t.name}
              onClick={() => handleSelectTable(t.name)}
              className={`w-full text-left px-3 py-2 text-sm transition-colors border-b last:border-0 ${
                selectedTable === t.name
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'hover:bg-accent/50 text-foreground/85'
              }`}
            >
              <span className="block truncate font-mono text-xs">{t.name}</span>
              <span className="block text-[11px] text-muted-foreground">
                {t.rows.toLocaleString()} rows
              </span>
            </button>
          ))}
        </div>
      </Card>

      {/* Right: schema + rows */}
      <div className="flex-1 min-w-0 space-y-4">
        {!selectedTable && (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
              <Database className="h-8 w-8 opacity-30" />
              <p className="text-sm">Select a table to browse its schema and rows</p>
            </CardContent>
          </Card>
        )}

        {selectedTable && (
          <>
            <div>
              <h2 className="font-mono text-base font-semibold text-foreground">{selectedTable}</h2>
              {tableData && (
                <p className="kicker text-muted-foreground mt-0.5">
                  {tableData.total.toLocaleString()} row{tableData.total !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            {/* Schema */}
            <Card>
              <div className="px-4 py-2.5 border-b">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Schema</p>
              </div>
              <CardContent className="p-0">
                {tableLoading && (
                  <div className="p-4 space-y-2">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="h-4 rounded bg-muted/40 animate-pulse" style={{ width: `${50 + i * 8}%` }} />
                    ))}
                  </div>
                )}
                {!tableLoading && tableData && <SchemaTable columns={tableData.columns} />}
                {!tableLoading && tableError && (
                  <p className="p-4 text-sm text-destructive">{tableError}</p>
                )}
              </CardContent>
            </Card>

            {/* Rows */}
            <Card>
              <div className="px-4 py-2.5 border-b">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Rows</p>
              </div>
              <CardContent className="p-0">
                {tableLoading && (
                  <div className="divide-y divide-border">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-4 px-4 py-3">
                        <div className="h-3.5 w-28 rounded bg-muted/40 animate-pulse" />
                        <div className="h-3.5 w-40 rounded bg-muted/30 animate-pulse" />
                        <div className="h-3.5 w-24 rounded bg-muted/20 animate-pulse" />
                      </div>
                    ))}
                  </div>
                )}
                {!tableLoading && tableData && (
                  <DataTable
                    columns={tableData.columns.map((c) => c.column_name)}
                    rows={tableData.rows}
                  />
                )}
                {!tableLoading && tableError && (
                  <p className="p-4 text-sm text-destructive">{tableError}</p>
                )}
              </CardContent>
            </Card>

            {!tableLoading && !tableError && tableData && tableData.total > 0 && (
              <PaginationControls
                page={page}
                totalPages={totalPages}
                pageSize={PAGE_SIZE}
                total={tableData.total}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ── Query tab ─────────────────────────────────────────────────────────────────
function QueryTab() {
  const { runQuery } = useAdmin()

  const [sql, setSql] = useState('')
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<DbQueryResult | null>(null)
  const [queryError, setQueryError] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const handleRun = async () => {
    if (!sql.trim() || running) return
    setRunning(true)
    setResult(null)
    setQueryError(null)
    try {
      const res = await runQuery(sql.trim())
      if (res.success) {
        setResult(res)
      } else {
        setQueryError(res.message)
      }
    } catch (err: unknown) {
      // The backend's 400 body carries the useful reason (e.g. "Only a single
      // read-only SELECT statement is allowed"). Check it FIRST — AxiosError
      // extends Error, so an `instanceof Error` check would always win and
      // surface the generic "Request failed with status code 400" instead.
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
          ?? (err instanceof Error ? err.message : 'Query failed')
      setQueryError(msg)
    } finally {
      setRunning(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      handleRun()
    }
  }

  return (
    <div className="space-y-4">
      {/* Editor card */}
      <Card>
        <div className="px-4 py-3 border-b flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0" style={{ color: 'hsl(var(--warning, 38 92% 50%))' }} />
            <span className="text-xs font-medium text-muted-foreground">
              Read-only — SELECT / WITH statements only
            </span>
          </div>
          <Button
            size="sm"
            onClick={handleRun}
            disabled={running || !sql.trim()}
            className="shrink-0 h-7 px-3 text-xs gap-1.5"
          >
            {running ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Play className="h-3.5 w-3.5" />
            )}
            Run
          </Button>
        </div>
        <CardContent className="p-0">
          <textarea
            ref={textareaRef}
            value={sql}
            onChange={(e) => setSql(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="SELECT * FROM users LIMIT 25;"
            rows={8}
            spellCheck={false}
            className="w-full resize-none font-mono text-sm p-4 bg-transparent focus:outline-none text-foreground placeholder:text-muted-foreground/50"
          />
          <div className="px-4 pb-2">
            <p className="text-[11px] text-muted-foreground/60">
              Cmd/Ctrl+Enter to run
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Inline error */}
      {queryError && (
        <div className="rounded-lg border p-3 text-sm text-destructive bg-destructive/5 border-destructive/20">
          {queryError}
        </div>
      )}

      {/* Results */}
      {result && (
        <Card>
          <div className="px-4 py-2.5 border-b flex items-center justify-between">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Results — {result.rows.length.toLocaleString()} row{result.rows.length !== 1 ? 's' : ''}
            </p>
            {result.truncated && (
              <span className="text-xs text-muted-foreground bg-muted/60 px-2 py-0.5 rounded">
                Truncated to 1000 rows
              </span>
            )}
          </div>
          <CardContent className="p-0">
            <DataTable columns={result.columns} rows={result.rows} />
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────
export function DatabasePage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="brand-wordmark text-2xl">Database</h1>
        <p className="kicker text-muted-foreground mt-1">
          Browse tables and run read-only queries
        </p>
      </div>

      <Tabs defaultValue="browse">
        <TabsList>
          <TabsTrigger value="browse">Browse</TabsTrigger>
          <TabsTrigger value="query">Query</TabsTrigger>
        </TabsList>

        <TabsContent value="browse" className="mt-4">
          <BrowseTab />
        </TabsContent>

        <TabsContent value="query" className="mt-4">
          <QueryTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
